import { TFunction } from 'i18next';
import parsePhoneNumber from 'libphonenumber-js';
import type { NavigateFunction } from 'react-router-dom';
import { getLangNameFromCode as baseGetLangNameFromCode } from 'language-name-map';

import { Config, Routes } from 'config';
import { Nullable, CourseStep, JourneyStage } from 'types';

import { addParamsToUrl } from './query';

export const joinStrings = (
  values: Nullable<string>[],
  separator: string,
  placeholder: string | null = '-'
): string => {
  const data = values.filter((value) => value && value?.length > 0) as string[];
  return data.length === 0 ? (placeholder ?? '') : data.join(separator);
};

export const stringToBoolean = (value: string): boolean => value === 'true';

export const booleanToString = (value: boolean): string =>
  value ? 'true' : 'false';

export const codeNameToJourneyStep = (codeName: string) => {
  const enumValues = Object.values(JourneyStage) as string[];
  const mainPartOfCode = codeName.split('_')[0];
  const type = enumValues.includes(mainPartOfCode)
    ? (mainPartOfCode as JourneyStage)
    : codeName;
  return type;
};

export const getNavigationPath = (
  currentStepCodeName: string,
  nextStep: CourseStep
) => {
  const { id, resourceVersions } = nextStep;
  const isResource = resourceVersions && resourceVersions.length > 0;
  if (isResource) {
    return Routes.Course.Resource.replace(':id', id);
  } else {
    return Routes.Course.Form.replace(':id', id);
  }
};

export const isPromise = (value: any) => {
  return (
    value != null &&
    typeof value === 'object' &&
    typeof value.then === 'function'
  );
};

export const formatPhone = (
  phone?: string,
  placeholder: string = '-',
  national: boolean = true
) => {
  if (!phone) {
    return placeholder;
  }
  const phoneNumber = parsePhoneNumber(phone);
  if (!phoneNumber) {
    return phone;
  }
  return national
    ? phoneNumber.formatNational()
    : phoneNumber.formatInternational();
};

type Language = { name: string; native: string; dir: 'ltr' | 'rtl' };

const fallbackLangs: Record<string, Language> = {
  hmn: { dir: 'ltr', name: 'Hmong', native: 'Hmoob' },
};

export const getLangNameFromCode = (langCode: string): Language | undefined => {
  const lang = baseGetLangNameFromCode(langCode);

  if (lang) {
    return lang;
  }

  return fallbackLangs[langCode];
};

export const getStepTitle = (
  t: TFunction,
  type: string,
  step: CourseStep,
  language?: string
) => {
  const translated = t(type);
  if (type !== translated) {
    return translated;
  }
  return (language && step?.translations?.[language]?.name) || step?.name;
};

export const clearCacheAndReload = async () => {
  const keyList = await caches.keys();
  await Promise.all(keyList.map((key) => caches.delete(key)));

  window.location.reload();
};

export const getWistiaId = (videoUrl: string): string | undefined =>
  videoUrl.match(/wistia\.com\/medias\/([a-z0-9]+)/i)?.[1];

const isAllowedAuthRedirectUrl = (url: URL): boolean => {
  if (url.origin === window.location.origin) {
    return true;
  }

  const allowedOrigins = Config.authRedirectUrlOrigins;

  return (
    allowedOrigins.includes('*') ||
    allowedOrigins.includes(url.origin.toLowerCase())
  );
};

const resolveAuthCallbackRedirect = (
  authCode: string,
  state?: string | null,
  redirectUrl?: string | null
): { url: string; useWindowLocation: boolean } => {
  if (redirectUrl) {
    try {
      const parsedRedirectUrl = new URL(redirectUrl, window.location.origin);

      if (isAllowedAuthRedirectUrl(parsedRedirectUrl)) {
        parsedRedirectUrl.searchParams.set('authCode', authCode);
        if (state) {
          parsedRedirectUrl.searchParams.set('state', state);
        }
        return { useWindowLocation: true, url: parsedRedirectUrl.toString() };
      }
    } catch (error) {
      console.error('Invalid auth redirect_url', error);
    }
  }

  return {
    useWindowLocation: false,
    url: addParamsToUrl(Routes.Auth.Callback, {
      authCode,
      ...(state ? { state } : {}),
    }),
  };
};

export const buildAuthCallbackUrl = (
  authCode: string,
  state?: string | null,
  redirectUrl?: string | null
) => resolveAuthCallbackRedirect(authCode, state, redirectUrl).url;

export const redirectToAuthCallback = ({
  state,
  navigate,
  authCode,
  redirectUrl,
}: {
  authCode: string;
  state?: string | null;
  navigate: NavigateFunction;
  redirectUrl?: string | null;
}) => {
  const { url, useWindowLocation } = resolveAuthCallbackRedirect(
    authCode,
    state,
    redirectUrl
  );

  if (useWindowLocation) {
    window.location.replace(url);
    return;
  }

  navigate(url, { replace: true });
};
